import React from "react";
import { createPortal } from "react-dom";
import { Dropdown, Spinner } from "react-bootstrap";
import { FiEye, FiPaperclip } from "react-icons/fi";

import type { DepartmentReportScope } from "@core/reports/api/requestDepartmentReportSave";
import type { DepartmentReportMediaItem } from "@core/reports/api/types";
import {
  buildDepartmentReportMediaArgs,
  viewDepartmentReportMediaInNewTab,
} from "@core/reports/api/departmentReportMedia";
import ReportMediaDetailsModal from "@core/reports/components/ReportMediaDetailsModal";
import { formatFileSize } from "@core/reports/constants/mediaAttachments";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";

type Props = {
  /** The child entity's scope ("church" or "district") and its own ids. */
  scope: DepartmentReportScope;
  entityId: number;
  departmentId: number;
  departmentReportId: number | null;
  media: DepartmentReportMediaItem[];
};

// Compact paperclip + count toggle (no caret). Stops propagation so opening the
// popover doesn't toggle the tree row's expansion.
const PaperclipToggle = React.forwardRef<
  HTMLButtonElement,
  { count: number; onClick?: (e: React.MouseEvent) => void }
>(({ count, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-sm btn-link text-decoration-none p-0 d-inline-flex align-items-center gap-1"
    title={`${count} attachment${count === 1 ? "" : "s"}`}
    aria-label={`${count} attachment${count === 1 ? "" : "s"}`}
    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onClick?.(e);
    }}
  >
    <FiPaperclip aria-hidden />
    <span className="small">{count}</span>
  </button>
));
PaperclipToggle.displayName = "PaperclipToggle";

// Mount the menu under document.body so sticky/overflow table ancestors cannot clip it.
// react-bootstrap injects show/close/align onto custom `as` components — omit them from the DOM.
const PortalDropdownMenu = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    show?: boolean;
    close?: () => void;
    align?: unknown;
  }
>(function PortalDropdownMenu({ show: _show, close: _close, align: _align, ...props }, ref) {
  return createPortal(<div ref={ref} {...props} />, document.body);
});

// react-bootstrap 2.10's polymorphic Dropdown mis-types children under React 19 — cast to a
// permissive FC (same workaround used in features/admin/invitations/InviteScopeSelect).
const DropdownRoot = Dropdown as unknown as React.FC<React.PropsWithChildren<Record<string, unknown>>>;

/**
 * Read-only attachment list for a CHILD entity in a composite report tree.
 * Renders a "📎 N" toggle (only when the child has media) opening a small menu;
 * each file opens in a new tab via the child-scoped, hierarchically-authorized endpoint.
 * View details opens a modal with full captions and metadata.
 */
export default function ChildMediaPopover({
  scope,
  entityId,
  departmentId,
  departmentReportId,
  media,
}: Props) {
  const [viewingId, setViewingId] = React.useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  if (!media || media.length === 0 || !departmentReportId) return null;

  const handleView = async (item: DepartmentReportMediaItem) => {
    if (!item.media_storage_id) return;
    setViewingId(item.media_storage_id);
    const message = await viewDepartmentReportMediaInNewTab(
      buildDepartmentReportMediaArgs(
        scope,
        entityId,
        departmentId,
        departmentReportId,
        item.media_storage_id
      )
    );
    if (message) useErrorModalStore.getState().showValidation([message]);
    setViewingId(null);
  };

  return (
    // Stop propagation so opening the popover doesn't toggle the tree row's expansion.
    <span
      className="d-inline-block"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <DropdownRoot>
        <Dropdown.Toggle as={PaperclipToggle} count={media.length} />
        <Dropdown.Menu
          as={PortalDropdownMenu}
          renderOnMount
          popperConfig={{
            strategy: "fixed",
            modifiers: [
              { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
            ],
          }}
        >
          <Dropdown.Header>Attachments</Dropdown.Header>
          {media.map((item, index) => (
            <Dropdown.Item
              as="button"
              type="button"
              key={`${item.media_storage_id}-${index}`}
              onClick={() => handleView(item)}
              className="d-flex align-items-center gap-2"
            >
              {viewingId === item.media_storage_id ? (
                <Spinner animation="border" size="sm" aria-hidden />
              ) : (
                <FiEye aria-hidden />
              )}
              <span className="text-truncate" style={{ maxWidth: 220 }}>
                {item.document_name ? `${item.document_name}: ` : ""}
                {item.filename || "Attachment"}
              </span>
              <span className="text-muted small ms-auto">
                {formatFileSize(item.size_bytes)}
              </span>
            </Dropdown.Item>
          ))}
          <Dropdown.Divider />
          <Dropdown.Item as="button" type="button" onClick={() => setDetailsOpen(true)}>
            View details
          </Dropdown.Item>
        </Dropdown.Menu>
      </DropdownRoot>

      <ReportMediaDetailsModal
        show={detailsOpen}
        onHide={() => setDetailsOpen(false)}
        items={media}
        onViewFile={handleView}
        viewingId={viewingId}
      />
    </span>
  );
}
